import type { NationalCoverage, PermitActivity } from '@mollusk/shared';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

/** A coverage percentage plus the label + methodology tooltip shown beneath it. */
function CoverageStat({
  coverage,
  label,
  methodology,
  emphasis,
}: {
  /** 0..1 fraction, or null to render "—" (data not loaded yet). */
  coverage: number | null;
  label: string;
  methodology: string;
  /** `primary` colours the number teal like a headline stat; `secondary` is plain. */
  emphasis: 'primary' | 'secondary';
}) {
  return (
    <Box>
      <Typography variant="h4" color={emphasis === 'primary' ? 'primary' : 'text.primary'}>
        {coverage == null ? '—' : `${(coverage * 100).toFixed(0)}%`}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Tooltip title={methodology} arrow placement="bottom">
          <InfoOutlinedIcon
            aria-label={`How we calculate ${label}`}
            sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }}
          />
        </Tooltip>
      </Box>
    </Box>
  );
}

/** Prominent national rollup shown above each coverage map: the weighted
 *  coverage % and the total number of records we hold, with a tooltip
 *  explaining how the percentage is calculated. An optional `secondary`
 *  coverage stat sits alongside the primary one — used by the permit map to
 *  show "believable" vs "any data on record" side by side (see #101). */
export function CoverageHeadline({
  national,
  weightLabel,
  recordNoun,
  methodology,
  recordValue,
  recordEstimated,
  coverageLabel = 'national coverage',
  secondary,
  activity,
}: {
  national: NationalCoverage | null;
  weightLabel: string;
  recordNoun: string;
  /** Plain-language explanation of how the (primary) coverage % is computed. */
  methodology: string;
  /** Override the record count. `undefined` → use national.record_count;
   *  `null` → render "—" (unknown, e.g. estimate pending). */
  recordValue?: number | null;
  /** Prefix the record count with "≈" to flag it as an estimate. */
  recordEstimated?: boolean;
  /** Label under the primary coverage %. Defaults to "national coverage". */
  coverageLabel?: string;
  /** A second coverage % shown next to the primary one (e.g. the ungated
   *  "any data on record" figure beside the gated "believable" one). */
  secondary?: { coverage: number; label: string; methodology: string };
  /** Fresh + located permit stats shown after the record count — what a data
   *  batch actually moves when the total barely changes (#165). */
  activity?: PermitActivity | null;
}) {
  const rawRecord = recordValue === undefined ? (national?.record_count ?? null) : recordValue;
  const recordText =
    rawRecord == null ? '—' : `${recordEstimated ? '≈' : ''}${rawRecord.toLocaleString()}`;
  return (
    <Box
      sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}
      data-testid="coverage-headline"
    >
      <CoverageStat
        coverage={national ? national.coverage : null}
        label={`${coverageLabel} (${weightLabel})`}
        methodology={methodology}
        emphasis="primary"
      />
      {national == null ? null : secondary ? (
        <>
          <Divider orientation="vertical" flexItem />
          <CoverageStat
            coverage={secondary.coverage}
            label={`${secondary.label} (${weightLabel})`}
            methodology={secondary.methodology}
            emphasis="secondary"
          />
        </>
      ) : null}
      <Divider orientation="vertical" flexItem />
      <Box>
        <Typography variant="h4">{recordText}</Typography>
        <Typography variant="caption" color="text.secondary">
          {recordNoun} on record
        </Typography>
      </Box>
      {activity ? (
        <>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="h4">{activity.permits_last_30d.toLocaleString()}</Typography>
            <Typography variant="caption" color="text.secondary">
              issued in the last 30 days
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="h4">{`${(activity.located_share * 100).toFixed(0)}%`}</Typography>
            <Typography variant="caption" color="text.secondary">
              located ({activity.located_count.toLocaleString()} of{' '}
              {(rawRecord ?? 0).toLocaleString()})
            </Typography>
          </Box>
        </>
      ) : null}
    </Box>
  );
}
